'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { Progress } from '@/shared/ui/shadcn/progress';
import { anglesService } from '@/shared/api/services/angles.service';
import { uploadsService } from '@/shared/api/services/uploads.service';
import { cacheInvalidation } from '@/shared/lib/cache-invalidation';
import { useConvertMaterialType } from '@/shared/api/hooks/use-material-convert';
import { useUpdateProjectReference } from '@/features/project-reference/hooks/use-project-references';
import { ApiClientError } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import type { UploadAngle } from '@/types/upload';
import { useUnifiedProgress } from '@/features/angle/hooks/use-unified-progress';
import {
  type ValidationItem,
  type UploadPhase,
  type UploadProgress,
  useValidationPolling,
  useValidationListener,
  useValidationReconcile,
  ValidationItemsList,
  decodeValidation,
} from './upload';

interface UnifiedUploadModalProps {
  isOpen: boolean;
  phase: UploadPhase;
  uploadProgress?: UploadProgress;
  /** Local object URLs of the in-flight files — drives the per-file rows
   *  during the uploading phase (parity with Apple ImageStatusRow). */
  localPreviews?: string[];
  angles: UploadAngle[];
  /**
   * Documents (PDF/txt/md) picked in the SAME mixed batch as the images. They
   * upload independently (project references, Brief section) but are surfaced
   * here as read-only review cards so they aren't silently filed away. Each
   * carries its own lifecycle status (uploading → moved_to_documents → error);
   * they never participate in angle validation or the valid-subset handed to
   * onComplete. Empty for a docs-only or image-only batch.
   */
  documentItems?: ValidationItem[];
  /** Building the upload targets — enables the "It's my building" override on a
   *  per-image different-building rejection (P5.1). Absent on conceptId-only
   *  flows; the override simply isn't offered then. */
  projectId?: string;
  onClose: () => void;
  onCancel?: () => void;
  onComplete?: (validAngles: UploadAngle[]) => void;
  /** Wired only while the building has no usable view yet. A finished batch
   *  that adds none then leads with "Add a photo" (reopens the picker) and
   *  demotes Cancel / Done to secondary. Parity with Apple/Android. */
  onAddAnotherPhoto?: () => void;
}

/** What the window currently shows — mirrors Apple ValidationProgressView
 *  phases: uploading → validating → result (buttons) → finalizing (web-only
 *  preview preload between Continue and close). */
type DisplayState = 'uploading' | 'validating' | 'result' | 'finalizing';

export function UnifiedUploadModal({
  isOpen,
  phase,
  uploadProgress,
  localPreviews,
  angles,
  documentItems,
  projectId,
  onClose,
  onCancel,
  onComplete,
  onAddAnotherPhoto,
}: UnifiedUploadModalProps) {
  const t = useTranslations('angle.upload.modal');
  const tOptions = useTranslations('render.options');
  const tConvert = useTranslations('briefConvert');
  const [validationItems, setValidationItems] = useState<ValidationItem[]>([]);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  // View → Reference correction on an auto-accepted view — the SAME two-hop
  // chain the Brief type selector uses (view → doc via materials/convert, then
  // PATCH the fresh reference into the References section).
  const convertMaterial = useConvertMaterialType(projectId ?? '');
  const updateReference = useUpdateProjectReference(projectId ?? '');
  const [makingReferenceId, setMakingReferenceId] = useState<string | null>(
    null
  );

  // Claim a filed-elsewhere intake as a view — serves BOTH the strict "It's my
  // building" block AND the lenient "Use as a view" action on an "added as a
  // reference" row. The server turns the file into a view and removes the
  // reference it became. The local row flips to valid so it joins the valid set
  // and a now-all-valid batch can continue.
  const handleConfirmBuilding = useCallback(
    async (uploadId: string) => {
      if (!projectId) return;

      setConfirmingId(uploadId);
      try {
        const upload = await uploadsService.promote(projectId, uploadId);
        const angleId = upload.attributes.outcome?.id ?? undefined;

        setValidationItems((prev) =>
          prev.map((item) =>
            item.id === uploadId
              ? {
                  ...item,
                  status: 'valid' as ValidationItem['status'],
                  error: undefined,
                  angleId,
                  validation: { ...item.validation, is_valid: true },
                }
              : item
          )
        );

        // The view arrived and the reference left — refresh both shelves.
        cacheInvalidation.afterCreateProjectAngle(projectId);
      } catch (error) {
        toast.error(
          error instanceof ApiClientError
            ? error.getFirstError()
            : tOptions('confirmBuildingError')
        );
      } finally {
        setConfirmingId(null);
      }
    },
    [projectId, tOptions]
  );

  // "Make it a reference" on an auto-accepted view: the classifier read the
  // image as inspiration but the server kept it as a view by default; the user
  // confirms it WAS inspiration. View → Doc (materials/convert returns a NEW
  // reference id) → PATCH to section=reference. On success the row flips to
  // the standard "Moved to references" outcome; the convert hook already
  // reconciles the angles/references/project caches.
  const handleMakeReference = useCallback(
    async (uploadId: string) => {
      // The correction acts on the VIEW the intake produced — an auto-accepted
      // row always has one (docs/upload/ADR-002).
      const viewId = validationItems.find(
        (item) => item.id === uploadId
      )?.angleId;
      if (!viewId) return;

      setMakingReferenceId(uploadId);
      try {
        const created = await convertMaterial.mutateAsync({
          source_type: 'view',
          source_id: viewId,
          target_type: 'doc',
        });
        await updateReference.mutateAsync({
          referenceId: created.id,
          section: 'reference',
          aspects: ['style'],
        });
        setValidationItems((prev) =>
          prev.map((item) =>
            item.id === uploadId
              ? {
                  ...item,
                  status: 'moved_to_references' as ValidationItem['status'],
                  // The auto-accept note no longer applies; the row reads as a
                  // plain routed outcome now. No promote-back offer — the user
                  // just made the opposite call explicitly (re-typing stays
                  // available from the Brief grid).
                  note: undefined,
                  error: undefined,
                  validation: {
                    ...item.validation,
                    is_valid: false,
                    auto_accepted_as_view: false,
                    can_override_building: false,
                  },
                }
              : item
          )
        );
      } catch (error) {
        // Server 4xx (e.g. "view has renders") surfaces verbatim.
        toast.error(
          error instanceof ApiClientError
            ? error.getFirstError()
            : tConvert('toastFailed')
        );
      } finally {
        setMakingReferenceId(null);
      }
    },
    [validationItems, convertMaterial, updateReference, tConvert]
  );
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [hasCompletedPreview, setHasCompletedPreview] = useState(false);
  const completedRef = useRef(false);

  // Eased timer for the validating phase only — LLM validation emits no
  // progress signal, so a time-based curve is the honest option there. The
  // uploading phase shows real byte progress from `uploadProgress` instead.
  const unifiedProgress = useUnifiedProgress();

  // Fallback row text, one object for every path that settles a row. Server
  // copy wins whenever the server sent any.
  const rowCopy = useMemo(
    () => ({
      notSuitable: t('notSuitableImage'),
      technicalError: t('technicalError'),
    }),
    [t]
  );

  // Validation polling fallback
  const { clearPolling } = useValidationPolling({
    projectId: projectId ?? '',
    phase,
    validationItems,
    setValidationItems,
    copy: rowCopy,
    validationTimeoutMessage: t('validationTimeout'),
  });

  // WebSocket listener for validation updates
  useValidationListener({
    setValidationItems,
    copy: rowCopy,
  });

  // L2/L3 reconcile: the socket can miss the announcement while it is down or
  // the tab is hidden. Both ask the building what is still unfinished.
  useValidationReconcile({
    projectId: projectId ?? '',
    validationItems,
    setValidationItems,
    copy: rowCopy,
  });

  // Start the eased timer when validation begins (no progress signal there);
  // uploading shows real progress, so the timer stays off for that phase.
  useEffect(() => {
    if (isOpen && phase === 'validating') {
      unifiedProgress.start();
    } else if (!isOpen || phase === 'idle' || phase === 'uploading') {
      unifiedProgress.reset();
    }
  }, [isOpen, phase, unifiedProgress]);

  // A new batch starts here. Drop the previous batch's rows with it: the window
  // stays mounted between uploads, and leaving them in place shows the finished
  // result of the last batch over the one now uploading — including its preview
  // URLs, which their owner has already released.
  useEffect(() => {
    if (!isOpen || phase === 'idle' || phase === 'uploading') {
      setValidationItems([]);
      setHasCompletedPreview(false);
    }
  }, [isOpen, phase]);

  // Initialize validation items when phase changes to validating
  useEffect(() => {
    if (phase === 'validating' && angles.length > 0) {
      const items: ValidationItem[] = angles.map((angle) => {
        const validation = angle.attributes?.validation;

        // The server says whether it is still reading; the client does not
        // keep its own copy of which statuses are terminal.
        if (validation && validation.is_in_progress === false) {
          // Shared decoder — same lenient/strict different-building split and
          // routed-outcome mapping the poll + WS paths use.
          const decoded = decodeValidation(validation, rowCopy);
          return {
            id: angle.id,
            thumbnailUrl: angle.previewUrl,
            status: decoded.status,
            note: decoded.note,
            error: decoded.error,
            validation: decoded.validation,
          };
        }

        return {
          id: angle.id,
          thumbnailUrl: angle.previewUrl,
          status: 'validating' as ValidationItem['status'],
          validation: undefined,
        };
      });

      setValidationItems(items);
      completedRef.current = false;
    }
  }, [phase, angles, t]);

  // Handle loading preview and completing upload
  const handleLoadPreviewAndComplete = useCallback(async () => {
    if (completedRef.current) return;
    completedRef.current = true;

    try {
      const validAngles = angles.filter((angle) => {
        const validationItem = validationItems.find(
          (item) => item.id === angle.id
        );
        return validationItem?.status === 'valid';
      });

      if (validAngles.length === 0) {
        console.warn('[UnifiedUploadModal] All angles were invalid or deleted');
        unifiedProgress.complete();
        if (onComplete) {
          onComplete([]);
        }
        return;
      }

      setIsLoadingPreview(true);

      const updatedAngles = await Promise.all(
        validAngles.map(async (angle) => {
          try {
            // The intake names the view it created; the preview belongs to it.
            const viewId =
              validationItems.find((item) => item.id === angle.id)?.angleId ??
              angle.angleId;
            if (!viewId) {
              return angle;
            }

            const angleData = await anglesService.getAngle(viewId);
            return {
              ...angle,
              angleId: viewId,
              attributes: {
                ...angle.attributes,
                resized_file_url: angleData.attributes?.file?.url || null,
              },
            };
          } catch (error) {
            console.error(
              `[UnifiedUploadModal] Failed to fetch angle ${angle.id}:`,
              error
            );
            return angle;
          }
        })
      );

      const imagesToPreload = updatedAngles
        .map((angle) => angle.attributes.resized_file_url)
        .filter((url): url is string => url !== null && url !== '');

      await Promise.all(
        imagesToPreload.map(
          (url) =>
            new Promise<void>((resolve) => {
              const img = new Image();
              img.onload = () => resolve();
              img.onerror = () => {
                console.error(
                  `[UnifiedUploadModal] Failed to preload image: ${url}`
                );
                resolve();
              };
              img.src = url;
            })
        )
      );

      setIsLoadingPreview(false);
      setHasCompletedPreview(true);
      unifiedProgress.complete();
      if (onComplete) {
        onComplete(updatedAngles);
      }
    } catch (error) {
      console.error(
        '[UnifiedUploadModal] Error during preview loading:',
        error
      );
      setIsLoadingPreview(false);
      setHasCompletedPreview(true);
      unifiedProgress.complete();
      if (onComplete) {
        const validAngles = angles.filter((angle) => {
          const validationItem = validationItems.find(
            (item) => item.id === angle.id
          );
          return validationItem?.status === 'valid';
        });
        onComplete(validAngles);
      }
    }
  }, [angles, validationItems, onComplete, unifiedProgress]);

  // Nothing to dismiss: a refused intake creates nothing, so cancelling leaves
  // nothing behind (docs/upload/ADR-002). The row lives on only as a record of
  // the attempt, and the server sweeps it by age.

  const handleCancel = useCallback(() => {
    clearPolling();
    // Cancel is offered on a FINISHED batch: whatever the server accepted is
    // already a view, a reference, or the rescue flag on the building. Without
    // this the interface keeps showing the building as it was before.
    if (projectId) {
      cacheInvalidation.afterCreateProjectAngle(projectId);
    }
    // onClose is the ONLY dismissal mechanism (it resets the owner's showModal +
    // phase), so it must ALWAYS fire — the dialog blocks escape/outside-click/
    // close-button and closes purely by isOpen flipping false. onCancel is a
    // supplemental cleanup notification, not a replacement: treating it as one
    // left the dialog stuck open when the owner (e.g. Brief's persistently
    // mounted hidden AngleUpload) never unmounts on cancel.
    onCancel?.();
    onClose();
  }, [onCancel, onClose, clearPolling, projectId]);

  // Same settle-and-close as Cancel, then hand back to the owner's picker.
  // Called synchronously inside the click so the file dialog keeps the user
  // activation browsers require to open it.
  const handleAddAnotherPhoto = useCallback(() => {
    handleCancel();
    onAddAnotherPhoto?.();
  }, [handleCancel, onAddAnotherPhoto]);

  // Continue (all valid) and Continue anyway (partial success) share the same
  // path: drop the invalid rows, then preload previews and hand the valid
  // subset to the parent. Mirrors Apple confirmComplete().
  const handleContinue = useCallback(() => {
    clearPolling();
    void handleLoadPreviewAndComplete();
  }, [clearPolling, handleLoadPreviewAndComplete]);

  // The rows of THIS batch: every upload currently in flight, paired with its
  // row. Read by id, never by position or count — the window stays mounted
  // between batches, so rows left over from the previous one are still in
  // state for the moment before the new batch replaces them, and they are the
  // same shape and often the same number. Matching by id is what keeps a
  // finished batch from answering for the one that just started.
  const batchItems = useMemo(() => {
    const byId = new Map(validationItems.map((item) => [item.id, item]));
    return angles.map((angle) => byId.get(angle.id));
  }, [angles, validationItems]);

  // Every upload of this batch has an answer. An upload with no row of its own
  // yet is still in flight, so the window waits.
  const allValidated =
    batchItems.length > 0 &&
    batchItems.every((item) => item !== undefined && item.status !== 'validating');

  const hasErrors = batchItems.some(
    (item) =>
      item !== undefined &&
      (item.status === 'invalid' || item.status === 'error')
  );

  // Our side failed, not the photo. ADR-002 repeats this one for free, so the
  // window must stay up and say so — it used to close on it without a word.
  const technicalErrorCount = validationItems.filter(
    (item) => item.status === 'error' && item.validation?.is_technical_error
  ).length;
  const rejectedCount = validationItems.filter(
    (item) => item.status === 'invalid'
  ).length;

  // Valid/invalid split drives the post-validation actions — partial success
  // offers "Continue anyway" with the valid subset (parity with Apple/Android).
  const hasValidItems = validationItems.some((item) => item.status === 'valid');

  // Any image auto-routed (server soft-deleted the angle): drawing→documents,
  // inspiration→references, artistic→output style, or — lenient mode — a
  // different-building photo→references as a plain reference. All NEUTRAL/POSITIVE
  // "moved/added, not failed" outcomes the user should read, so we DON'T
  // auto-dismiss; the result screen stays up with an explicit button.
  const hasMovedToDocuments = validationItems.some(
    (item) => item.status === 'moved_to_documents'
  );
  const hasMovedToReferences = validationItems.some(
    (item) => item.status === 'moved_to_references'
  );
  const hasMovedToOutputStyle = validationItems.some(
    (item) => item.status === 'moved_to_output_style'
  );
  // Lenient different-building → "Added as a reference" (has a "Use as a view"
  // forward action). Treated as a non-failure routed outcome for framing.
  const hasAddedAsReference = validationItems.some(
    (item) => item.status === 'added_as_reference'
  );
  const hasMovedItems =
    hasMovedToDocuments ||
    hasMovedToReferences ||
    hasMovedToOutputStyle ||
    hasAddedAsReference;

  // STRICT different-building hard block (concept add-view): needs the user to
  // confirm "It's my building" or cancel. Not a routed outcome, not a plain
  // failure — it gates auto-continue and drives the strict window framing.
  const hasStrictBlock = validationItems.some(
    (item) => item.status === 'different_building_block'
  );

  // Auto-accepted view (valid, but the classifier read it as inspiration and
  // the server kept it as a view by default). Valid for every downstream
  // purpose, but it must NOT auto-dismiss the window — the user should read
  // the server note and get the chance to file it as a reference instead.
  const hasAutoAcceptedView = validationItems.some(
    (item) =>
      item.status === 'valid' &&
      item.validation?.auto_accepted_as_view === true
  );

  // Mixed-batch documents (PDF/txt/md uploaded alongside the images). They have
  // their own upload lifecycle and DON'T validate. Their presence keeps the
  // result screen up (suppresses silent auto-continue) so the user actually
  // sees the "Classified as document" cards before the window closes.
  const docs = documentItems ?? [];
  const hasDocuments = docs.length > 0;
  // Gate the forward action until every document has finished uploading, so the
  // user sees its terminal "Classified as document" card (rule: completion waits
  // for documents too). Uploads are fast, so this disables Continue only briefly.
  const docsSettled = docs.every((d) => d.status !== 'uploading');

  // All images valid → continue automatically, no button. The result screen with
  // explicit actions exists only for failures, routed/"moved" outcomes (so the
  // server message is read), and strict blocks. completedRef (inside
  // handleLoadPreviewAndComplete) makes re-fires idempotent. The server holds the
  // angle in `processing` for the whole same-building check, so the poll only ever
  // reads the final terminal status — a late verdict arrives as a normal terminal
  // row before the gate releases (API is the source of truth; no wait heuristic).
  useEffect(() => {
    if (
      phase === 'validating' &&
      allValidated &&
      !hasErrors &&
      !hasMovedItems &&
      !hasStrictBlock &&
      !hasAutoAcceptedView &&
      !hasDocuments &&
      !completedRef.current
    ) {
      handleContinue();
    }
  }, [
    phase,
    allValidated,
    hasErrors,
    hasMovedItems,
    hasStrictBlock,
    hasAutoAcceptedView,
    hasDocuments,
    handleContinue,
  ]);

  const displayState: DisplayState =
    phase === 'uploading'
      ? 'uploading'
      : isLoadingPreview || hasCompletedPreview
        ? 'finalizing'
        : allValidated
          ? 'result'
          : 'validating';

  // Single continuous bar across both phases, like Apple overallProgress:
  // upload (real bytes) maps to 0–50%, validation (eased timer) to 50–100%.
  const overallProgress =
    displayState === 'uploading'
      ? Math.round((uploadProgress?.percentage ?? 0) / 2)
      : 50 + Math.round(unifiedProgress.progress / 2);

  // The result is a "mixed/classification" outcome when any row was routed
  // (moved/added) or strict-blocked, OR when failures sit alongside accepted
  // rows. The window is then a CLASSIFICATION review — it must NOT shout
  // "Validation failed" / "All images failed". Only a batch that is PURELY plain
  // failures keeps the hard failure framing.
  const hasClassifiedOutcome = hasMovedItems || hasStrictBlock;
  const isPureFailure = hasErrors && !hasValidItems && !hasClassifiedOutcome;

  const phaseTitle = (() => {
    switch (displayState) {
      case 'uploading':
        return t('phaseUploading');
      case 'validating':
        return t('phaseValidating');
      case 'finalizing':
        return t('phaseLoadingPreview');
      case 'result':
        // Strict add-view block — the one thing the user must act on.
        if (hasStrictBlock) {
          return t('needsYourInput');
        }
        // Purely plain failures (no accepted/routed rows) keep the failure title.
        if (isPureFailure) {
          // Nothing was wrong with the photos — say that instead of blaming them.
          return rejectedCount === 0 && technicalErrorCount > 0
            ? t('technicalErrorTitle')
            : t('validationFailed');
        }
        // Anything mixed (failures + accepted, or any routed outcome) reads as a
        // neutral classification review, never a failure.
        if (hasErrors || (hasClassifiedOutcome && hasValidItems)) {
          return t('reviewUploads');
        }
        // No failures, no valid photos — a pure "moved/added" outcome. Single-kind
        // moves get a precise title; any mix falls back to the generic one.
        if (hasClassifiedOutcome && !hasValidItems) {
          if (
            hasAddedAsReference &&
            !hasMovedToOutputStyle &&
            !hasMovedToReferences &&
            !hasMovedToDocuments
          ) {
            return t('addedAsReferenceTitle');
          }
          if (
            hasMovedToOutputStyle &&
            !hasMovedToReferences &&
            !hasMovedToDocuments &&
            !hasAddedAsReference
          ) {
            return t('movedToOutputStyleTitle');
          }
          if (
            hasMovedToReferences &&
            !hasMovedToOutputStyle &&
            !hasMovedToDocuments &&
            !hasAddedAsReference
          ) {
            return t('movedToReferencesTitle');
          }
          if (
            hasMovedToDocuments &&
            !hasMovedToOutputStyle &&
            !hasMovedToReferences &&
            !hasAddedAsReference
          ) {
            return t('movedToDocumentsTitle');
          }
          // Any remaining classified/routed mix (e.g. an output style alongside a
          // reference) has no single-category header — use the neutral "Files added"
          // rather than mislabeling it as one category. Per-card labels carry the
          // precise outcome.
          return t('filesAdded');
        }
        // Valid photos plus mixed-batch documents (no failures/routes): the
        // documents are the only "extra" outcome to acknowledge → "Files added"
        // reads truer than the bare "Complete".
        if (hasDocuments) {
          return t('filesAdded');
        }
        return t('phaseComplete');
    }
  })();

  const phaseIcon = (() => {
    switch (displayState) {
      // Uploading/validating/finalizing all show the static brand mark (the
      // origami house) — no top spinner. The progress bar conveys progress; the
      // per-row spinners convey per-file work. Parity with Apple/Android.
      case 'uploading':
      case 'finalizing':
      case 'validating':
        return <img src="/logo.svg" alt="" className="h-10 w-10" />;
      case 'result':
        // Amber attention glyph for a pure failure or a strict block (the user
        // must act); a calm green check for accepted/classified outcomes.
        return isPureFailure || hasStrictBlock ? (
          <AlertTriangle className="h-10 w-10 text-orange-500" />
        ) : (
          <CheckCircle2 className="h-10 w-10 text-green-600 dark:text-green-400" />
        );
    }
  })();

  // Per-file rows in every phase — during uploading the server rows don't
  // exist yet, so the local previews stand in (same "one window throughout"
  // feel as Apple/Android). Mixed-batch document cards are appended AFTER the
  // image rows in every phase (their own upload lifecycle, no validation).
  const displayItems: ValidationItem[] = [
    ...(displayState === 'uploading'
      ? (localPreviews ?? []).map((url) => ({
          id: url,
          thumbnailUrl: url,
          status: 'uploading' as const,
        }))
      : validationItems),
    ...docs,
  ];

  // Dismissal mirrors Apple: no X, no Escape, no outside-click. Not because
  // anything would be orphaned — an intake creates nothing until it settles —
  // but because the outcome is the point: a photo filed as a reference, or a
  // building the server read as another one, needs an answer, not a stray
  // click. Every exit goes through the result-phase buttons (Continue /
  // Continue anyway / Cancel).
  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className={[
          'flex flex-col gap-6 min-h-[420px]',
          // Narrow: full-screen takeover, like Apple's fullScreenCover.
          'max-sm:left-0 max-sm:top-0 max-sm:translate-x-0 max-sm:translate-y-0',
          // max-h-none: the base DialogContent caps sheets at max-h-[90dvh];
          // without the reset the takeover stops 10dvh short of the bottom and
          // the tab bar peeks through under the modal.
          'max-sm:h-dvh max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:rounded-none',
          // Wide: fixed card, like the Apple macOS 450×400 sheet.
          'sm:w-[450px] sm:max-w-[450px] sm:max-h-[85vh]',
        ].join(' ')}
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">{t('title')}</DialogTitle>

        {/* Phase indicator — icon + title (+ failure summary) */}
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-[50px] items-center justify-center">
            {phaseIcon}
          </div>
          <p className="text-sheet-title">{phaseTitle}</p>
          {/* The count line is about photos that were refused. A technical
              failure is not one of them — its own row carries the server text. */}
          {displayState === 'result' && isPureFailure && rejectedCount > 0 && (
            <p className="text-list-item-subtitle text-muted-foreground px-4">
              {t('allFailed')}
            </p>
          )}
          {displayState === 'result' &&
            !isPureFailure &&
            rejectedCount > 0 && (
              <p className="text-list-item-subtitle text-muted-foreground px-4">
                {t('partialFailed', {
                  invalid: rejectedCount,
                  total: validationItems.length,
                })}
              </p>
            )}
          {displayState === 'result' &&
            !hasErrors &&
            hasStrictBlock &&
            // Per-row server copy already explains the strict block; no extra
            // subtitle so the card's own message and CTA stay the focus.
            null}
        </div>

        {/* Overall progress — hidden once validation has finished */}
        {(displayState === 'uploading' || displayState === 'validating') && (
          <div className="space-y-2">
            <Progress value={overallProgress} className="w-full" />
            <p className="text-progress-meta text-center text-muted-foreground">
              {t('percentComplete', { progress: overallProgress })}
            </p>
          </div>
        )}

        <ValidationItemsList
          items={displayItems}
          onConfirmBuilding={handleConfirmBuilding}
          confirmingId={confirmingId}
          // The view → reference chain needs the project scope; without it the
          // action simply isn't offered (parity with confirm-building gating).
          onMakeReference={projectId ? handleMakeReference : undefined}
          makingReferenceId={makingReferenceId}
        />

        {/* Reassure the user the classification isn't permanent: a routed file
            lives in the same unified grid and can be re-typed later from its own
            settings. Only shown when something was actually classified/routed. */}
        {displayState === 'result' && hasClassifiedOutcome && (
          <p className="text-card-meta text-muted-foreground text-center px-4">
            {t('classificationHint')}
          </p>
        )}

        {/* Action buttons — result phase only, stacked like Apple.
            - STRICT add-view block (unresolved): the ONLY decision lives in the
              card ("It's my building"); the window's bottom action is Cancel,
              which adds nothing (default). Once the block is resolved to a valid
              view, it falls through to the normal Continue path below.
            - PURE failure (junk only, no accepted rows): just Cancel.
            - Otherwise (valid and/or routed/"moved" rows): a primary Continue
              carries the valid subset forward and acknowledges the moved rows;
              a secondary Cancel appears only when plain failures are mixed in. */}
        {displayState === 'result' && (
          <div className="flex flex-col items-center gap-3">
            {onAddAnotherPhoto && !hasValidItems ? (
              <>
                <Button onClick={handleAddAnotherPhoto}>{t('addPhoto')}</Button>
                {hasErrors || hasStrictBlock ? (
                  <Button variant="outline" onClick={handleCancel}>
                    {t('cancel')}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={handleContinue}
                    disabled={!docsSettled}
                  >
                    {t('done')}
                  </Button>
                )}
              </>
            ) : hasStrictBlock ? (
              <Button variant="outline" onClick={handleCancel}>
                {t('cancel')}
              </Button>
            ) : isPureFailure ? (
              <Button variant="outline" onClick={handleCancel}>
                {t('cancel')}
              </Button>
            ) : (
              <>
                <Button onClick={handleContinue} disabled={!docsSettled}>
                  {hasErrors
                    ? t('continueAnyway')
                    : // A purely "moved/added" outcome carries no photo forward —
                      // "Done" reads truer than "Continue".
                      hasClassifiedOutcome && !hasValidItems
                      ? t('done')
                      : t('continue')}
                </Button>
                {hasErrors && (
                  <Button variant="outline" onClick={handleCancel}>
                    {t('cancel')}
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
